import React from 'react';
import { GraduationCap, BookOpen, FileCheck, Plane, Home, Briefcase, ChevronRight } from 'lucide-react';

interface DashboardHubProps {
    tileStatuses: Record<string, string>;
    onTileClick: (id: string) => void;
}

const services = [
    { id: 'admissions', label: 'Admissions', icon: GraduationCap, description: 'University applications & documents' },
    { id: 'accommodation', label: 'Accommodation', icon: Home, description: 'Housing search & guidance' },
    { id: 'visa_lab', label: 'Visa Lab', icon: FileCheck, description: 'Visa compliance & tracking' },
    { id: 'prep_lab', label: 'English Prep', icon: BookOpen, description: 'Language preparation & testing' },
    { id: 'travel', label: 'Travel', icon: Plane, description: 'Travel planning & logistics' },
    { id: 'career_launchpad', label: 'Career Launchpad', icon: Briefcase, description: 'Career readiness & placement' },
];

const DashboardHub: React.FC<DashboardHubProps> = ({ tileStatuses, onTileClick }) => {

    const getStatusConfig = (status: string) => {
        switch (status) {
            case 'completed':
                return { color: '#00e5d0', bg: 'rgba(0,229,208,0.10)', label: 'COMPLETE' };
            case 'in-progress':
                return { color: '#ffa726', bg: 'rgba(255,167,38,0.10)', label: 'IN PROGRESS' };
            case 'attention':
                return { color: '#ba1a1a', bg: 'rgba(186,26,26,0.08)', label: 'ATTENTION' };
            default:
                return { color: 'rgba(25,28,30,0.25)', bg: 'rgba(25,28,30,0.04)', label: 'NOT STARTED' };
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((service, index) => {
                const status = tileStatuses[service.id] || 'not-started';
                const statusConfig = getStatusConfig(status);
                const Icon = service.icon;

                return (
                    <button
                        key={service.id}
                        onClick={() => onTileClick(service.id)}
                        className="group relative p-6 rounded-2xl text-left transition-all duration-300 animate-fadeInUp"
                        style={{
                            background: '#ffffff',
                            boxShadow: '0 1px 3px rgba(0,52,52,0.04), 0 4px 16px rgba(0,52,52,0.03)',
                            animationDelay: `${index * 60}ms`,
                        }}
                        onMouseEnter={e => {
                            (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 24px rgba(0,52,52,0.08), 0 8px 32px rgba(0,52,52,0.05)';
                            (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={e => {
                            (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,52,52,0.04), 0 4px 16px rgba(0,52,52,0.03)';
                            (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                        }}
                    >
                        {/* Icon + Status Row */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="p-3 rounded-xl transition-colors duration-300"
                                style={{ background: 'rgba(0,52,52,0.04)' }}>
                                <Icon size={22} strokeWidth={1.8} style={{ color: '#003434' }}
                                    className="group-hover:scale-110 transition-transform duration-300" />
                            </div>

                            {/* Status Orb */}
                            <div className="flex items-center gap-2 px-2.5 py-1 rounded-full"
                                style={{ background: statusConfig.bg }}>
                                <span className="w-1.5 h-1.5 rounded-full animate-pulseOrb"
                                    style={{ background: statusConfig.color }} />
                                <span className="text-[9px] font-ui font-bold uppercase tracking-widest"
                                    style={{ color: statusConfig.color }}>
                                    {statusConfig.label}
                                </span>
                            </div>
                        </div>

                        {/* Label */}
                        <h3 className="text-base font-display font-bold mb-1.5"
                            style={{ color: '#191c1e' }}>
                            {service.label}
                        </h3>

                        {/* Description */}
                        <p className="text-sm font-ui leading-relaxed mb-4"
                            style={{ color: 'rgba(25,28,30,0.5)' }}>
                            {service.description}
                        </p>

                        {/* Action Row */}
                        <div className="flex items-center gap-1 text-xs font-ui font-bold uppercase tracking-wider"
                            style={{ color: '#004d4d' }}>
                            <span>Open</span>
                            <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

export default DashboardHub;
